import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { segmentedRecipe } from './segmented-control';

function Tabs({
  className,
  orientation = 'horizontal',
  ...props
}: Readonly<TabsPrimitive.Root.Props>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  'group/tabs-list relative inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        line: 'gap-1 bg-transparent',
        /* VOLL: degrau nosso, não do shadcn (pedido do designer olhando a `SubPagina`). A trilha,
           o respiro de 2px e a pílula deslizante do `SegmentedControl` — a receita única do
           controle; aqui só a semântica é de aba. `p-0.5` no lugar do `p-[3px]`: em raio de pílula
           a sobra ímpar aparece como anel torto na quina. */
        pill: 'isolate rounded-full bg-sand-3 p-0.5',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function TabsList({
  className,
  variant = 'default',
  children,
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    >
      {children}
      {variant === 'pill' && <TabsPillIndicator />}
    </TabsPrimitive.List>
  );
}

/**
 * A pílula da variante `pill`: o thumb do `SegmentedControl` (cartão, sombra, curva de 300ms),
 * posto pela medida que o Base UI publica da aba ativa. Vem da própria lista, nunca do
 * consumidor: aba em pílula sem pílula deslizante foi o que fez o controle ter duas caras.
 */
function TabsPillIndicator() {
  return (
    <TabsPrimitive.Indicator
      data-slot="tabs-pill-indicator"
      className={cn(
        segmentedRecipe.thumb,
        'pointer-events-none absolute top-0.5 bottom-0.5 left-(--active-tab-left) w-(--active-tab-width) rounded-full transition-[left,width] duration-300 ease-[cubic-bezier(0.19,1,0.22,1)] motion-reduce:transition-none',
      )}
    />
  );
}

function TabsTrigger({ className, ...props }: Readonly<TabsPrimitive.Tab.Props>) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 aria-disabled:pointer-events-none aria-disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
        'data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
        /* VOLL: o gatilho herda a pílula da trilha, e ganha ar lateral — em forma redonda o rótulo
           precisa de mais recuo que os 6px do retângulo pra não encostar na curva. O fundo do
           ativo é da pílula deslizante: o gatilho fica transparente, acima dela, com os tons do
           `SegmentedControl`. */
        'group-data-[variant=pill]/tabs-list:rounded-full group-data-[variant=pill]/tabs-list:px-3',
        'group-data-[variant=pill]/tabs-list:z-10 group-data-[variant=pill]/tabs-list:h-full group-data-[variant=pill]/tabs-list:text-muted-foreground group-data-[variant=pill]/tabs-list:hover:text-foreground group-data-[variant=pill]/tabs-list:data-active:bg-transparent group-data-[variant=pill]/tabs-list:data-active:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * O stroke deslizante da variante `line` — vai DENTRO do `TabsList`, depois das abas.
 *
 * O Base UI mede a aba ativa (e re-mede em resize) e publica `--active-tab-left/width`; o que entra
 * aqui é só a curva do `segmented-control` — 300ms, desaceleração longa —, pra o indicador do sistema
 * ter um jeito só de andar. Ele substitui o `::after` estático que a variante `line` tinha: dois
 * marcadores pro mesmo ativo se atropelariam.
 */
function TabsIndicator({ className, ...props }: Readonly<TabsPrimitive.Indicator.Props>) {
  return (
    <TabsPrimitive.Indicator
      data-slot="tabs-indicator"
      className={cn(
        'bg-foreground absolute bottom-0 left-[var(--active-tab-left)] h-0.5 w-[var(--active-tab-width)] rounded-full transition-[left,width] duration-300 ease-[cubic-bezier(0.19,1,0.22,1)] motion-reduce:transition-none',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: Readonly<TabsPrimitive.Panel.Props>) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn('flex-1 text-sm outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsIndicator, TabsContent, tabsListVariants };
